import { useCallback, useMemo, useState } from 'react';

import type { AggregatedStat, DonationForm } from '@feathr/blackbox';

import { useStore } from '../../state';
import type { TDateRange, TPreset } from './DonationFormReport.utils';
import { presetToRange, shiftWindow } from './DonationFormReport.utils';

export interface IUseDonationFormReportProps {
  /** The donation form being reported on. */
  form: DonationForm;
}

export interface IUseDonationFormReportReturn {
  /** Aggregated stats for the selected date range. */
  currentStats: AggregatedStat;
  /** Aggregated stats for the immediately preceding window (same length). */
  previousStats: AggregatedStat;
  /** The currently selected preset (`'7d' | '30d' | '90d' | 'custom'`). */
  preset: TPreset;
  /** The resolved `[startISO, endISO]` range for the current window. */
  range: TDateRange;
  /** The resolved `[startISO, endISO]` range for the immediately preceding window (same length). */
  previousRange: TDateRange;
  /** Updates the preset and re-derives the range (no-op for `'custom'`). */
  setPreset: (preset: TPreset) => void;
  /** Updates the range directly (intended for `preset === 'custom'`). */
  setRange: (range: TDateRange) => void;
  /** True while either the current or previous aggregate request is in flight. */
  isLoading: boolean;
}

const DEFAULT_PRESET: TPreset = '7d';

/**
 * Encapsulates date-range state + two `AggregatedStats.aggregate()` fetches
 * (current window + immediately preceding window) for the donation form
 * report. The preceding window is used to compute per-tile delta percentages.
 *
 * The `AggregatedStats` collection caches by hashed params (see
 * `stats_aggregated.ts`), so changing the preset or range is inexpensive —
 * repeat fetches for the same window reuse the cached reactive model.
 *
 * @example
 * const { currentStats, previousStats, preset, range, setPreset, setRange, isLoading } =
 *   useDonationFormReport({ form });
 */
export function useDonationFormReport({
  form,
}: IUseDonationFormReportProps): IUseDonationFormReportReturn {
  const store = useStore();
  const [preset, setPresetState] = useState<TPreset>(DEFAULT_PRESET);
  const [range, setRangeState] = useState<TDateRange>(() =>
    presetToRange(DEFAULT_PRESET, ['', '']),
  );

  const setPreset = useCallback((next: TPreset): void => {
    setPresetState(next);
    if (next !== 'custom') {
      setRangeState((prev) => presetToRange(next, prev));
    }
  }, []);

  const setRange = useCallback((next: TDateRange): void => {
    setRangeState(next);
  }, []);

  const previousRange = useMemo<TDateRange>(() => shiftWindow(range), [range]);

  const currentStats = store.AggregatedStats.aggregate({
    filters: {
      metadata__obj_id: form.id,
      metadata__date__gte: range[0],
      metadata__date__lte: range[1],
    },
    model: 'form',
  });

  const previousStats = store.AggregatedStats.aggregate({
    filters: {
      metadata__obj_id: form.id,
      metadata__date__gte: previousRange[0],
      metadata__date__lte: previousRange[1],
    },
    model: 'form',
  });

  const isLoading = currentStats.isPending || previousStats.isPending;

  return {
    currentStats,
    previousStats,
    preset,
    range,
    previousRange,
    setPreset,
    setRange,
    isLoading,
  };
}
